import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PiWarningCircleLight, PiSealCheckLight, PiBriefcaseLight, PiBuildingsLight, PiCameraLight, PiCaretLeftLight, PiCheckLight, PiCheckCircleLight, PiClipboardTextLight, PiArrowSquareOutLight, PiGlobeLight, PiHandshakeLight, PiInfoLight, PiStackLight, PiLinkLight as LinkIcon, PiCircleNotchLight, PiMapPinLight, PiNewspaperLight, PiPlusLight, PiFloppyDiskLight, PiShieldCheckLight, PiSparkleLight, PiTrashLight, PiUploadSimpleLight } from 'react-icons/pi';
import toast from 'react-hot-toast';
import { authAPI, employerAPI } from '../../services/api';
import { API_BASE_URL } from '../../services/apiService';
import { useAuthStore } from '../../store';

const tabs = [
  { id: 'overview', label: 'Overview', icon: PiBuildingsLight },
  { id: 'culture', label: 'Culture', icon: PiHandshakeLight },
  { id: 'sections', label: 'Profile Sections', icon: PiStackLight },
  { id: 'news', label: 'News & Links', icon: PiNewspaperLight },
  { id: 'security', label: 'Security', icon: PiShieldCheckLight },
  { id: 'verification', label: 'Verification', icon: PiShieldCheckLight }
];

const companySizes = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'];
const companyTypes = [
  { value: 'startup', label: 'Startup' },
  { value: 'private', label: 'Private' },
  { value: 'public', label: 'Public' },
  { value: 'non_profit', label: 'Non-profit' },
  { value: 'government', label: 'Government' }
];
const industries = [
  { value: 'technology', label: 'Technology' },
  { value: 'finance', label: 'Finance' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'education', label: 'Education' },
  { value: 'retail', label: 'Retail' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'media', label: 'Media' },
  { value: 'non_profit', label: 'Non-profit' },
  { value: 'government', label: 'Government' },
  { value: 'startups', label: 'Startups' },
  { value: 'other', label: 'Other' }
];
const sectionTypes = [
  { value: 'about', label: 'About' },
  { value: 'mission', label: 'Mission' },
  { value: 'culture', label: 'Culture' },
  { value: 'values', label: 'Values' },
  { value: 'benefits', label: 'Benefits' },
  { value: 'team', label: 'Team' },
  { value: 'hiring_process', label: 'Hiring Process' },
  { value: 'workplace', label: 'Workplace' },
  { value: 'news', label: 'News' }
];
const socialFields = [
  ['linkedin', 'LinkedIn'],
  ['twitter', 'Twitter / X'],
  ['facebook', 'Facebook'],
  ['instagram', 'Instagram'],
  ['youtube', 'YouTube'],
  ['github', 'GitHub'],
  ['glassdoor', 'Glassdoor'],
  ['careers', 'Careers page']
];

const defaultCore = {
  companyName: '',
  jobTitle: '',
  industry: '',
  sector: '',
  companySize: '',
  employeeCountRange: '',
  companyType: '',
  foundedYear: '',
  websiteUrl: '',
  headquartersLocation: '',
  countryCode: '',
  contactEmail: '',
  contactPhone: '',
  companyDescription: '',
  goalsAndVision: '',
  businessRegistrationNumber: '',
  taxIdentificationNumber: '',
  registeredAddress: {
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: ''
  }
};

const defaultCulture = {
  values: [''],
  benefits: [''],
  workEnvironment: '',
  diversity: '',
  operatingModel: '',
  collaborationStyle: '',
  learningAndGrowth: '',
  managerSupport: '',
  hiringPrinciples: '',
  companyStory: ''
};

const cultureSignalFields = [
  { key: 'workEnvironment', label: 'Work environment' },
  { key: 'diversity', label: 'Diversity' },
  { key: 'operatingModel', label: 'Operating model' },
  { key: 'collaborationStyle', label: 'Collaboration style' },
  { key: 'learningAndGrowth', label: 'Learning and growth' },
  { key: 'managerSupport', label: 'Manager support' }
];

const defaultSocials = socialFields.reduce((acc, [key]) => ({ ...acc, [key]: '' }), {});

const defaultSections = [
  { sectionType: 'about', title: 'About', content: '', contentType: 'text', mediaUrls: [], displayOrder: 0, isPublished: true },
  { sectionType: 'culture', title: 'Culture', content: '', contentType: 'text', mediaUrls: [], displayOrder: 1, isPublished: true },
  { sectionType: 'benefits', title: 'Benefits', content: '', contentType: 'text', mediaUrls: [], displayOrder: 2, isPublished: true },
  { sectionType: 'team', title: 'Team', content: '', contentType: 'text', mediaUrls: [], displayOrder: 3, isPublished: true }
];

const normalizeText = (value) => (value === null || value === undefined ? '' : String(value));
const normalizeDate = (value) => value ? String(value).slice(0, 10) : '';
const compact = (value) => normalizeText(value).trim();
const numberOrNull = (value) => compact(value) ? Number(value) : null;
const sanitizeCountryCodeInput = (value) => {
  const cleaned = normalizeText(value).replace(/[^a-zA-Z0-9+]/g, '').toUpperCase();
  const singlePlus = cleaned.startsWith('+')
    ? `+${cleaned.slice(1).replace(/\+/g, '')}`
    : cleaned.replace(/\+/g, '');

  return singlePlus.startsWith('+') ? singlePlus.slice(0, 6) : singlePlus.slice(0, 5);
};

const asTextArray = (value) => {
  const source = Array.isArray(value) ? value : [];
  const normalized = source
    .map((item) => {
      if (typeof item === 'string') return item;
      if (!item || typeof item !== 'object') return '';
      return [item.title, item.description].filter(Boolean).join(': ');
    })
    .map(normalizeText);

  return normalized.length ? normalized : [''];
};

const toCultureDraftValue = (key, value) => (
  ['values', 'benefits'].includes(key) ? asTextArray(value) : value
);

const asArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return [value].filter(Boolean);
};

const isEmptyAnswer = (value) => {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

const isEmptyDraftValue = (value) => {
  if (Array.isArray(value)) return value.every((item) => !compact(item));
  if (value && typeof value === 'object') return Object.values(value).every(isEmptyDraftValue);
  return !compact(value);
};

const formatSuggestionValue = (value) => {
  if (Array.isArray(value)) return value.map((item) => typeof item === 'string' ? item : JSON.stringify(item)).join(', ');
  if (value && typeof value === 'object') return value.content || JSON.stringify(value);
  return normalizeText(value);
};

const getObjectListFields = (question) => (
  Array.isArray(question.validationRules?.fields) && question.validationRules.fields.length
    ? question.validationRules.fields
    : ['name', 'notes']
);

const createObjectListItem = (question) => (
  getObjectListFields(question).reduce((acc, field) => ({ ...acc, [field]: '' }), {})
);

const fieldLabel = (value) => (
  normalizeText(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const getProfileQuestionHelpText = (question) => {
  if (question?.questionKey === 'company_benefits_offered') {
    return 'Add each benefit separately. Include type, eligibility, and notes only where they help.';
  }

  return question?.helpText;
};

const normalizeObjectListValue = (question, rawValue) => {
  if (Array.isArray(rawValue)) return rawValue;
  if (!compact(rawValue)) return [];

  try {
    const parsed = JSON.parse(rawValue);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [];
  }
};

const cleanObjectListValue = (question, rawValue) => {
  const fields = getObjectListFields(question);
  const items = normalizeObjectListValue(question, rawValue);

  return items
    .filter((item) => item && typeof item === 'object' && !Array.isArray(item))
    .map((item) => fields.reduce((acc, field) => {
      const cleaned = compact(item[field]);
      return cleaned ? { ...acc, [field]: cleaned } : acc;
    }, {}))
    .filter((item) => Object.keys(item).length > 0);
};

const questionInitialValue = (question) => {
  const value = question.answer?.value;
  if (value === null || value === undefined) return '';
  if (question.answerType === 'object_list' && Array.isArray(value)) return value;
  if (question.answerType === 'text_array' && Array.isArray(value)) return value.join('\n');
  return value;
};

const toAnswerValue = (question, rawValue) => {
  if (isEmptyAnswer(rawValue)) return null;
  if (question.answerType === 'number' || question.answerType === 'scale') return Number(rawValue);
  if (question.answerType === 'boolean') return Boolean(rawValue);
  if (question.answerType === 'multi_select' || question.answerType === 'ranking') return asArray(rawValue);
  if (question.answerType === 'text_array') {
    return Array.isArray(rawValue)
      ? rawValue.map(compact).filter(Boolean)
      : String(rawValue).split(/\n|,/).map(compact).filter(Boolean);
  }
  if (question.answerType === 'object_list') {
    const cleaned = cleanObjectListValue(question, rawValue);
    if (cleaned.length) return cleaned;
    try {
      const parsed = JSON.parse(rawValue);
      const parsedCleaned = cleanObjectListValue(question, parsed);
      return parsedCleaned.length ? parsedCleaned : null;
    } catch (_error) {
      return null;
    }
  }
  return typeof rawValue === 'string' ? rawValue.trim() : rawValue;
};

const labelize = (value) => (
  normalizeText(value || 'pending')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const getEnrichmentStatusMessage = (snapshot) => {
  const run = snapshot?.run;
  if (!run) return '';
  if (['pending', 'processing'].includes(run.status)) {
    return `Analyzing your company sources: ${labelize(run.stage)} (${run.progress || 0}%).`;
  }
  const pendingCount = Number(snapshot.pendingCount || 0);
  const failedSourceCount = Number(run.summary?.failedSourceCount || 0);
  if (run.status === 'failed') {
    return 'Company source analysis could not be completed. Retry when ready.';
  }
  if (run.status === 'cancelled') {
    return 'Company source analysis was cancelled. No suggestions will be added.';
  }
  if (run.status === 'partial') {
    return `${pendingCount ? `${pendingCount} suggestions are ready.` : 'No new suggestions were found.'} ${failedSourceCount || 'One or more'} source${failedSourceCount === 1 ? '' : 's'} could not be analyzed and can be retried.`;
  }
  return pendingCount
    ? `${pendingCount} suggestions are ready. Suggested values have only filled empty draft fields and require your review.`
    : 'Company source analysis is complete and there are no pending suggestions.';
};

const statusClass = (status) => {
  const normalized = normalizeText(status).toLowerCase();
  if (['verified', 'approved'].includes(normalized)) return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (['under_review', 'in_review'].includes(normalized)) return 'border-amber-200 bg-amber-50 text-amber-700';
  if (normalized === 'rejected') return 'border-rose-200 bg-rose-50 text-rose-700';
  return 'border-sky-200 bg-sky-50 text-sky-700';
};

const resolveAssetUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;

  try {
    return `${new URL(API_BASE_URL).origin}${url.startsWith('/') ? url : `/${url}`}`;
  } catch (error) {
    return url;
  }
};

const hydrateCore = (employer = {}) => ({
  ...defaultCore,
  companyName: normalizeText(employer.companyName || employer.company_name),
  jobTitle: normalizeText(employer.jobTitle || employer.job_title),
  industry: normalizeText(employer.industry),
  sector: normalizeText(employer.sector),
  companySize: normalizeText(employer.companySize || employer.company_size),
  employeeCountRange: normalizeText(employer.employeeCountRange || employer.employee_count_range),
  companyType: normalizeText(employer.companyType || employer.company_type),
  foundedYear: normalizeText(employer.foundedYear || employer.founded_year),
  websiteUrl: normalizeText(employer.websiteUrl || employer.website || employer.website_url),
  headquartersLocation: normalizeText(employer.headquartersLocation || employer.headquarters || employer.headquarters_location),
  countryCode: normalizeText(employer.countryCode || employer.country?.code).toUpperCase(),
  contactEmail: normalizeText(employer.contactEmail || employer.contact?.email),
  contactPhone: normalizeText(employer.contactPhone),
  companyDescription: normalizeText(employer.companyDescription || employer.company_description),
  goalsAndVision: normalizeText(employer.goalsAndVision || employer.goals_and_vision),
  businessRegistrationNumber: normalizeText(employer.businessRegistrationNumber),
  taxIdentificationNumber: normalizeText(employer.taxIdentificationNumber),
  registeredAddress: {
    ...defaultCore.registeredAddress,
    ...(employer.registeredAddress || {})
  }
});

const hydrateSections = (sections = []) => {
  if (!Array.isArray(sections) || sections.length === 0) return defaultSections;

  return sections.map((section, index) => ({
    id: section.id,
    sectionType: section.sectionType || section.section_type || 'about',
    title: normalizeText(section.title),
    content: normalizeText(section.content),
    contentType: section.contentType || section.content_type || 'text',
    mediaUrls: Array.isArray(section.mediaUrls || section.media_urls) ? (section.mediaUrls || section.media_urls) : [],
    displayOrder: Number(section.displayOrder ?? section.display_order ?? index),
    isPublished: section.isPublished ?? section.is_published ?? true
  }));
};

const Panel = ({ title, icon: Icon, action, children, className = '', headerClassName = '', id }) => (
  <section id={id} className={`rounded-lg border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.045)] ${className}`}>
    <div className={`border-b border-slate-100 px-4 py-3 sm:px-5 ${headerClassName}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {Icon && (
            <span className="flex shrink-0 items-center justify-center text-sky-600">
              <Icon size={30} />
            </span>
          )}
          <h2 className="truncate font-display text-[17px] font-normal text-[#0b1f3a]">{title}</h2>
        </div>
        {action}
      </div>
    </div>
    <div className="p-4 sm:p-5">{children}</div>
  </section>
);

const Field = ({ label, error, children, className = '' }) => (
  <label className={`block min-w-0 ${className}`}>
    <span className="mb-1.5 block text-xs font-medium uppercase tracking-[0.12em] text-slate-500">{label}</span>
    {children}
    {error && <span className="mt-1.5 block text-xs font-medium text-rose-600">{error}</span>}
  </label>
);

const inputClass = 'h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-sky-300 focus:ring-4 focus:ring-sky-100';
const textareaClass = 'min-h-[112px] w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm leading-6 text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-sky-300 focus:ring-4 focus:ring-sky-100';

const SaveButton = ({ loading, children = 'Save' }) => (
  <button
    type="submit"
    disabled={loading}
    className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#0b1f3a] px-4 text-sm font-medium text-white transition hover:bg-[#12345f] disabled:cursor-not-allowed disabled:opacity-65"
  >
    {loading ? <PiCircleNotchLight size={13} className="animate-spin" /> : <PiFloppyDiskLight size={13} />}
    {children}
  </button>
);

const EmptyState = ({ label }) => (
  <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
    {label}
  </div>
);

function ProfilePage() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const appliedProposalIdsRef = useRef(new Set());
  const dirtyCoreFieldsRef = useRef(new Set());
  const dirtyCultureFieldsRef = useRef(new Set());
  const dirtySocialFieldsRef = useRef(new Set());
  const dirtyQuestionKeysRef = useRef(new Set());
  const initialTab = tabs.some((tab) => tab.id === searchParams.get('tab')) ? searchParams.get('tab') : 'overview';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [profile, setProfile] = useState(null);
  const [kyc, setKyc] = useState(null);
  const [core, setCore] = useState(defaultCore);
  const [culture, setCulture] = useState(defaultCulture);
  const [sections, setSections] = useState(defaultSections);
  const [socialLinks, setSocialLinks] = useState(defaultSocials);
  const [news, setNews] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [savingTab, setSavingTab] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const [createMode, setCreateMode] = useState(false);
  const [profileSetup, setProfileSetup] = useState(null);
  const [questionAnswers, setQuestionAnswers] = useState({});
  const [skippedAnswers, setSkippedAnswers] = useState({});
  const [questionErrors, setQuestionErrors] = useState({});
  const [activeCompanyQuestionIndex, setActiveCompanyQuestionIndex] = useState(0);
  const [savingQuestionKey, setSavingQuestionKey] = useState('');
  const [securitySaving, setSecuritySaving] = useState(false);
  const [dirtyQuestionKeys, setDirtyQuestionKeys] = useState({});
  const [recentlySavedQuestionKeys, setRecentlySavedQuestionKeys] = useState({});
  const [companyQuestionnaireFinished, setCompanyQuestionnaireFinished] = useState(false);
  const [cultureSignalsReviewing, setCultureSignalsReviewing] = useState(false);
  const [enrichment, setEnrichment] = useState(null);
  const [enrichmentAction, setEnrichmentAction] = useState('');

  const hydrate = useCallback((employer) => {
    dirtyCoreFieldsRef.current = new Set();
    dirtyCultureFieldsRef.current = new Set();
    dirtySocialFieldsRef.current = new Set();
    setProfile(employer);
    setCreateMode(!employer?.id);
    setCore(hydrateCore(employer));
    setCulture({
      ...defaultCulture,
      ...(employer.companyCulture || {}),
      values: asTextArray(employer.companyCulture?.values),
      benefits: asTextArray(employer.companyCulture?.benefits)
    });
    setSections(hydrateSections(employer.profileSections));
    setSocialLinks({ ...defaultSocials, ...(employer.socialLinks || {}) });
    setNews(Array.isArray(employer.companyNews) ? employer.companyNews.map((item) => ({
      title: normalizeText(item.title),
      content: normalizeText(item.content),
      date: normalizeDate(item.date),
      url: normalizeText(item.url)
    })) : []);
  }, []);

  const hydrateProfileQuestions = useCallback((setup) => {
    const questions = setup?.questions || [];
    const nextAnswers = {};
    const nextSkipped = {};

    questions.forEach((question) => {
      if (question.answer) {
        nextAnswers[question.questionKey] = questionInitialValue(question);
        nextSkipped[question.questionKey] = Boolean(question.answer.skipped);
      }
    });

    setQuestionAnswers(nextAnswers);
    setSkippedAnswers(nextSkipped);
    setQuestionErrors({});
    setDirtyQuestionKeys({});
    dirtyQuestionKeysRef.current = new Set();
    setRecentlySavedQuestionKeys({});

    const firstUnansweredIndex = questions.findIndex((question) => !question.answer || question.answer.skipped);
    const allQuestionsAnswered = questions.length > 0 && firstUnansweredIndex === -1;
    setCompanyQuestionnaireFinished(allQuestionsAnswered);
    setActiveCompanyQuestionIndex((currentIndex) => {
      if (!questions.length) return 0;
      if (!allQuestionsAnswered) return firstUnansweredIndex;

      return Math.min(Math.max(currentIndex, 0), questions.length - 1);
    });
  }, []);

  const hydrateEnrichment = useCallback((snapshot) => {
    const normalized = snapshot?.data || snapshot || null;
    setEnrichment(normalized);
    const proposals = (normalized?.proposals || []).filter((proposal) => (
      proposal.status === 'pending' && !appliedProposalIdsRef.current.has(proposal.id)
    ));
    proposals.forEach((proposal) => appliedProposalIdsRef.current.add(proposal.id));
    if (!proposals.length) return;

    setCore((previous) => proposals.reduce((next, proposal) => {
      if (proposal.targetType !== 'core' || !Object.prototype.hasOwnProperty.call(next, proposal.targetKey)) return next;
      if (dirtyCoreFieldsRef.current.has(proposal.targetKey)) return next;
      if (!isEmptyDraftValue(next[proposal.targetKey])) return next;
      return {
        ...next,
        [proposal.targetKey]: proposal.targetKey === 'foundedYear'
          ? normalizeText(proposal.proposedValue)
          : proposal.proposedValue
      };
    }, previous));
    setCulture((previous) => proposals.reduce((next, proposal) => {
      if (proposal.targetType !== 'culture' || !Object.prototype.hasOwnProperty.call(next, proposal.targetKey)) return next;
      if (dirtyCultureFieldsRef.current.has(proposal.targetKey)) return next;
      if (!isEmptyDraftValue(next[proposal.targetKey])) return next;
      return { ...next, [proposal.targetKey]: toCultureDraftValue(proposal.targetKey, proposal.proposedValue) };
    }, previous));
    setSocialLinks((previous) => proposals.reduce((next, proposal) => {
      if (proposal.targetType !== 'social_link' || !Object.prototype.hasOwnProperty.call(next, proposal.targetKey)) return next;
      if (dirtySocialFieldsRef.current.has(proposal.targetKey)) return next;
      if (!isEmptyDraftValue(next[proposal.targetKey])) return next;
      return { ...next, [proposal.targetKey]: proposal.proposedValue };
    }, previous));
    setQuestionAnswers((previous) => proposals.reduce((next, proposal) => {
      if (proposal.targetType !== 'profile_question' || !isEmptyAnswer(next[proposal.targetKey])) return next;
      if (dirtyQuestionKeysRef.current.has(proposal.targetKey)) return next;
      return { ...next, [proposal.targetKey]: proposal.proposedValue };
    }, previous));
  }, []);

  const refreshEnrichment = useCallback(async () => {
    try {
      hydrateEnrichment(await employerAPI.getProfileEnrichment());
      return true;
    } catch (_error) {
      return false;
    }
  }, [hydrateEnrichment]);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    appliedProposalIdsRef.current = new Set();

    const [profileResult, kycResult, setupResult, enrichmentResult] = await Promise.allSettled([
      employerAPI.getProfile(),
      employerAPI.getKycStatus(),
      employerAPI.getProfileSetup(),
      employerAPI.getProfileEnrichment()
    ]);

    if (profileResult.status === 'fulfilled') {
      const employer = profileResult.value?.employer || profileResult.value?.data?.employer || profileResult.value;
      hydrate(employer || {});
    } else if (profileResult.reason?.status === 404 || profileResult.reason?.response?.status === 404) {
      setCreateMode(true);
      hydrate({});
    } else {
      setLoadError(profileResult.reason?.message || 'Employer profile could not be loaded.');
    }

    if (kycResult.status === 'fulfilled') {
      setKyc(kycResult.value);
    }

    if (setupResult.status === 'fulfilled') {
      const setup = setupResult.value?.data || setupResult.value;
      setProfileSetup(setup);
      hydrateProfileQuestions(setup);
    } else if (![404, 503].includes(setupResult.reason?.status || setupResult.reason?.response?.status)) {
      console.warn('Employer profile question setup unavailable', setupResult.reason?.message || setupResult.reason);
    }

    if (enrichmentResult.status === 'fulfilled') {
      hydrateEnrichment(enrichmentResult.value);
    }

    setLoading(false);
  }, [hydrate, hydrateEnrichment, hydrateProfileQuestions]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    if (!['pending', 'processing'].includes(enrichment?.run?.status)) return undefined;
    const timer = window.setInterval(refreshEnrichment, 3000);
    return () => window.clearInterval(timer);
  }, [enrichment?.run?.status, refreshEnrichment]);

  useEffect(() => {
    const requestedTab = searchParams.get('tab');
    if (tabs.some((tab) => tab.id === requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [searchParams]);

  useEffect(() => {
    if (activeTab !== 'culture' || searchParams.get('focus') !== 'company-questions') return;

    const timeout = window.setTimeout(() => {
      document.getElementById('company-profile-questions')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 80);

    return () => window.clearTimeout(timeout);
  }, [activeTab, searchParams, profileSetup]);

  const selectProfileTab = (tabId) => {
    setActiveTab(tabId);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', tabId);
    nextParams.delete('focus');
    setSearchParams(nextParams, { replace: true });
  };

  const applyAcceptedProposalLocally = (proposal) => {
    if (!proposal) return;
    if (proposal.targetType === 'core' && !dirtyCoreFieldsRef.current.has(proposal.targetKey)) {
      setCore((previous) => ({ ...previous, [proposal.targetKey]: normalizeText(proposal.proposedValue) }));
    } else if (proposal.targetType === 'culture' && !dirtyCultureFieldsRef.current.has(proposal.targetKey)) {
      setCulture((previous) => ({
        ...previous,
        [proposal.targetKey]: toCultureDraftValue(proposal.targetKey, proposal.proposedValue)
      }));
    } else if (proposal.targetType === 'social_link' && !dirtySocialFieldsRef.current.has(proposal.targetKey)) {
      setSocialLinks((previous) => ({ ...previous, [proposal.targetKey]: proposal.proposedValue }));
    } else if (proposal.targetType === 'profile_question' && !dirtyQuestionKeysRef.current.has(proposal.targetKey)) {
      setQuestionAnswers((previous) => ({ ...previous, [proposal.targetKey]: proposal.proposedValue }));
      setRecentlySavedQuestionKeys((previous) => ({ ...previous, [proposal.targetKey]: true }));
      setProfileSetup((previous) => {
        if (!previous) return previous;
        const questions = previous.questions || [];
        const targetQuestion = questions.find((question) => question.questionKey === proposal.targetKey);
        const addsAnswer = Boolean(targetQuestion && (!targetQuestion.answer || targetQuestion.answer.skipped));
        const replacesSkipped = Boolean(targetQuestion?.answer?.skipped);
        const totalQuestions = Number(previous.questionProgress?.totalQuestions || questions.length);
        const answeredQuestions = Number(previous.questionProgress?.answeredQuestions || 0) + Number(addsAnswer);
        return {
          ...previous,
          questionProgress: {
            ...(previous.questionProgress || {}),
            totalQuestions,
            answeredQuestions,
            skippedQuestions: Math.max(0, Number(previous.questionProgress?.skippedQuestions || 0) - Number(replacesSkipped)),
            completionPercentage: totalQuestions ? Math.round((answeredQuestions / totalQuestions) * 100) : 0
          },
          questions: questions.map((question) => (
            question.questionKey === proposal.targetKey
              ? { ...question, answer: { value: proposal.proposedValue, skipped: false } }
              : question
          ))
        };
      });
    } else if (proposal.targetType === 'profile_section') {
      setSections((previous) => {
        const matchingIndex = previous.findIndex((section) => section.sectionType === proposal.targetKey);
        if (matchingIndex >= 0) {
          const matching = previous[matchingIndex];
          if (!isEmptyDraftValue(matching.content) || (matching.mediaUrls || []).some(Boolean)) return previous;
          return previous.map((section, index) => index === matchingIndex ? {
            ...section,
            title: normalizeText(proposal.proposedValue?.title) || section.title,
            content: normalizeText(proposal.proposedValue?.content),
            isPublished: false
          } : section);
        }
        return [...previous, {
          sectionType: proposal.targetKey,
          title: normalizeText(proposal.proposedValue?.title),
          content: normalizeText(proposal.proposedValue?.content),
          contentType: 'text',
          mediaUrls: [],
          displayOrder: previous.length,
          isPublished: false
        }];
      });
    }
  };

  const removeProposalDraftLocally = (proposal) => {
    if (!proposal) return;
    const matchesProposal = (value, expected = proposal.proposedValue) => (
      JSON.stringify(value) === JSON.stringify(expected) ||
      normalizeText(value) === normalizeText(expected)
    );
    if (proposal.targetType === 'core' && !dirtyCoreFieldsRef.current.has(proposal.targetKey)) {
      setCore((previous) => matchesProposal(previous[proposal.targetKey])
        ? { ...previous, [proposal.targetKey]: '' }
        : previous);
    } else if (proposal.targetType === 'culture' && !dirtyCultureFieldsRef.current.has(proposal.targetKey)) {
      setCulture((previous) => matchesProposal(
        previous[proposal.targetKey],
        toCultureDraftValue(proposal.targetKey, proposal.proposedValue)
      )
        ? { ...previous, [proposal.targetKey]: ['values', 'benefits'].includes(proposal.targetKey) ? [''] : '' }
        : previous);
    } else if (proposal.targetType === 'social_link' && !dirtySocialFieldsRef.current.has(proposal.targetKey)) {
      setSocialLinks((previous) => matchesProposal(previous[proposal.targetKey])
        ? { ...previous, [proposal.targetKey]: '' }
        : previous);
    } else if (proposal.targetType === 'profile_question' && !dirtyQuestionKeysRef.current.has(proposal.targetKey)) {
      setQuestionAnswers((previous) => matchesProposal(previous[proposal.targetKey])
        ? { ...previous, [proposal.targetKey]: '' }
        : previous);
    }
  };

  const markProposalStatusesLocally = (results = []) => {
    const statuses = new Map(results
      .filter((result) => result?.proposalId && ['accepted', 'rejected', 'superseded', 'skipped'].includes(result.status))
      .map((result) => [result.proposalId, result.status]));
    if (!statuses.size) return;

    setEnrichment((previous) => {
      if (!previous) return previous;
      const proposals = (previous.proposals || []).map((proposal) => (
        statuses.has(proposal.id) ? { ...proposal, status: statuses.get(proposal.id) } : proposal
      ));
      const pendingCount = proposals.filter((proposal) => proposal.status === 'pending').length;
      return {
        ...previous,
        proposals,
        pendingCount,
        safeAcceptableCount: Math.min(Number(previous.safeAcceptableCount || 0), pendingCount)
      };
    });
  };

  const reviewEnrichmentProposal = async (proposalId, decision) => {
    try {
      setEnrichmentAction(`${decision}:${proposalId}`);
      const proposal = (enrichment?.proposals || []).find((item) => item.id === proposalId);
      let reviewedStatus;
      if (decision === 'accept') {
        const response = await employerAPI.acceptProfileEnrichmentProposal(proposalId);
        reviewedStatus = response?.status;
        if (response?.status === 'accepted') {
          applyAcceptedProposalLocally(proposal);
          toast.success('Suggestion added to the company profile.');
        } else {
          removeProposalDraftLocally(proposal);
          toast(response?.status === 'rejected'
            ? 'That suggestion was already rejected.'
            : 'That suggestion was already superseded by newer profile information.');
        }
      } else {
        const response = await employerAPI.rejectProfileEnrichmentProposal(proposalId);
        reviewedStatus = response?.proposal?.status;
        if (response?.proposal?.status === 'accepted') applyAcceptedProposalLocally(proposal);
        else removeProposalDraftLocally(proposal);
      }
      markProposalStatusesLocally([{ proposalId, status: reviewedStatus }]);
      await refreshEnrichment();
    } catch (error) {
      toast.error(error?.message || 'Suggestion review failed.');
    } finally {
      setEnrichmentAction('');
    }
  };

  const acceptSafeEnrichment = async () => {
    if (!enrichment?.run?.id) return;
    try {
      setEnrichmentAction('accept-safe');
      const response = await employerAPI.acceptSafeProfileEnrichmentProposals(enrichment.run.id);
      const proposalsById = new Map((enrichment.proposals || []).map((proposal) => [proposal.id, proposal]));
      (response?.results || []).forEach((result) => {
        const proposal = proposalsById.get(result.proposalId);
        if (result.status === 'accepted') applyAcceptedProposalLocally(proposal);
        if (result.status === 'superseded') removeProposalDraftLocally(proposal);
      });
      markProposalStatusesLocally(response?.results || []);
      const failedSuffix = response?.failedCount ? ` ${response.failedCount} could not be applied.` : '';
      toast.success(`${response?.acceptedCount || 0} verified fact suggestions added.${failedSuffix}`);
      await refreshEnrichment();
    } catch (error) {
      toast.error(error?.message || 'Suggestions could not be accepted.');
    } finally {
      setEnrichmentAction('');
    }
  };

  const retryEnrichment = async () => {
    if (!enrichment?.run?.id) return;
    try {
      setEnrichmentAction('retry');
      const response = await employerAPI.retryProfileEnrichment(enrichment.run.id);
      if (response?.run) {
        setEnrichment((previous) => previous ? { ...previous, run: response.run } : previous);
      }
      await refreshEnrichment();
    } catch (error) {
      toast.error(error?.message || 'Company profile analysis could not be retried.');
    } finally {
      setEnrichmentAction('');
    }
  };

  const completion = useMemo(() => {
    const coreFields = [
      core.companyName,
      core.industry,
      core.companySize,
      core.companyType,
      core.headquartersLocation,
      core.countryCode,
      core.contactEmail,
      core.companyDescription,
      core.goalsAndVision,
      core.websiteUrl
    ];
    const cultureFields = [
      culture.workEnvironment,
      culture.diversity,
      culture.operatingModel,
      culture.collaborationStyle,
      culture.learningAndGrowth,
      ...culture.values,
      ...culture.benefits
    ];
    const sectionFields = sections.map((section) => section.content);
    const done = [...coreFields, ...cultureFields, ...sectionFields].filter((value) => compact(value)).length;
    const total = coreFields.length + cultureFields.length + sectionFields.length;
    return total ? Math.round((done / total) * 100) : 0;
  }, [core, culture, sections]);

  const cultureSignalStatus = useMemo(() => {
    const items = cultureSignalFields.map((field) => ({
      ...field,
      value: compact(culture[field.key])
    }));
    const completed = items.filter((item) => item.value).length;
    const total = items.length;

    return {
      items,
      completed,
      total,
      remaining: total - completed,
      percentage: total ? Math.round((completed / total) * 100) : 0,
      complete: total > 0 && completed === total
    };
  }, [culture]);

  const verificationStatus = kyc?.verificationStatus || profile?.verificationStatus || 'pending_documents';
  const logoUrl = resolveAssetUrl(profile?.companyLogoUrl);
  const documentsUploaded = Number(kyc?.kycCompletion?.total_uploaded ?? profile?.kycStatus?.documents_uploaded ?? 0);
  const documentsVerified = Number(kyc?.kycCompletion?.verified_count ?? profile?.kycStatus?.verified_documents ?? 0);
  const requiredDocuments = Number(kyc?.kycCompletion?.required_count ?? 0);
  const companyQuestionProgress = profileSetup?.questionProgress || {};
  const profileQuestions = profileSetup?.questions || [];
  const profileQuestionCount = profileQuestions.length;
  const safeCompanyQuestionIndex = profileQuestionCount
    ? Math.min(Math.max(activeCompanyQuestionIndex, 0), profileQuestionCount - 1)
    : 0;
  const currentCompanyQuestion = profileQuestions[safeCompanyQuestionIndex] || null;
  const currentCompanyQuestionKey = currentCompanyQuestion?.questionKey || '';
  const currentCompanyQuestionSection = currentCompanyQuestion
    ? fieldLabel(currentCompanyQuestion.section || currentCompanyQuestion.category || 'Company profile')
    : 'Company Profile';

  useEffect(() => {
    setActiveCompanyQuestionIndex((currentIndex) => {
      if (!profileQuestionCount) return 0;
      return Math.min(Math.max(currentIndex, 0), profileQuestionCount - 1);
    });
  }, [profileQuestionCount]);

  const updateCore = (key, value) => {
    dirtyCoreFieldsRef.current.add(key);
    setCore((previous) => ({ ...previous, [key]: value }));
    if (errors[key]) setErrors((previous) => ({ ...previous, [key]: '' }));
  };

  const updateAddress = (key, value) => {
    dirtyCoreFieldsRef.current.add('registeredAddress');
    setCore((previous) => ({
      ...previous,
      registeredAddress: {
        ...previous.registeredAddress,
        [key]: value
      }
    }));
  };

  const updateCultureSignal = (key, value) => {
    dirtyCultureFieldsRef.current.add(key);
    setCultureSignalsReviewing(true);
    setCulture((previous) => ({ ...previous, [key]: value }));
  };

  const validateCore = () => {
    const nextErrors = {};
    const required = createMode
      ? ['companyName', 'companyDescription', 'industry', 'companySize', 'companyType', 'headquartersLocation', 'countryCode', 'contactEmail']
      : ['companyName'];

    required.forEach((key) => {
      if (!compact(core[key])) nextErrors[key] = 'Required';
    });

    if (core.countryCode && !/^([a-z]{2,3}|\+?\d{1,5})$/i.test(compact(core.countryCode))) {
      nextErrors.countryCode = 'Use 2-3 letters or a dialing code up to 5 digits';
    }
    if (core.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(core.contactEmail)) nextErrors.contactEmail = 'Invalid email';
    if (core.foundedYear && (Number(core.foundedYear) < 1800 || Number(core.foundedYear) > new Date().getFullYear())) {
      nextErrors.foundedYear = 'Invalid year';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const saveCore = async (event) => {
    event.preventDefault();
    if (!validateCore()) return;

    const common = {
      companyName: compact(core.companyName),
      companyDescription: compact(core.companyDescription),
      industry: compact(core.industry),
      companySize: compact(core.companySize),
      foundedYear: numberOrNull(core.foundedYear),
      companyType: compact(core.companyType),
      contactEmail: compact(core.contactEmail),
      contactPhone: compact(core.contactPhone),
      businessRegistrationNumber: compact(core.businessRegistrationNumber),
      taxIdentificationNumber: compact(core.taxIdentificationNumber),
      registeredAddress: {
        ...core.registeredAddress,
        country: compact(core.registeredAddress.country).toUpperCase()
      }
    };

    const fullPayload = createMode
      ? {
        ...common,
        website: compact(core.websiteUrl),
        headquarters: compact(core.headquartersLocation),
        countryCode: compact(core.countryCode).toUpperCase()
      }
      : {
        ...common,
        jobTitle: compact(core.jobTitle),
        sector: compact(core.sector),
        websiteUrl: compact(core.websiteUrl),
        headquartersLocation: compact(core.headquartersLocation),
        countryCode: compact(core.countryCode).toUpperCase(),
        employeeCountRange: compact(core.employeeCountRange),
        goalsAndVision: compact(core.goalsAndVision)
      };
    const payload = createMode
      ? fullPayload
      : Object.fromEntries(
        Object.entries(fullPayload).filter(([key]) => dirtyCoreFieldsRef.current.has(key))
      );
    if (!createMode && Object.keys(payload).length === 0) {
      toast('No overview changes to save.');
      return;
    }

    try {
      setSavingTab('overview');
      const response = createMode
        ? await employerAPI.createProfile(payload)
        : await employerAPI.updateProfile(payload);

      toast.success(createMode ? 'Company profile created.' : 'Company profile saved.');
      const employer = response?.employer || response?.data?.employer;
      if (createMode) {
        await loadProfile();
      } else {
        const savedKeys = Array.from(dirtyCoreFieldsRef.current);
        dirtyCoreFieldsRef.current = new Set();
        if (employer) {
          setProfile(employer);
          const savedCore = hydrateCore(employer);
          setCore((previous) => savedKeys.reduce((next, key) => ({
            ...next,
            [key]: savedCore[key]
          }), previous));
        }
        await refreshEnrichment();
      }
    } catch (error) {
      toast.error(error?.message || 'Profile save failed.');
    } finally {
      setSavingTab('');
    }
  };

  const saveCulture = async (event) => {
    event.preventDefault();
    if (createMode) {
      toast.error('Create the company profile first.');
      setActiveTab('overview');
      return;
    }

    const dirtyKeys = Array.from(dirtyCultureFieldsRef.current);
    if (!dirtyKeys.length) {
      toast('No culture changes to save.');
      return;
    }
    const companyCulture = Object.fromEntries(dirtyKeys.map((key) => [
      key,
      ['values', 'benefits'].includes(key)
        ? (culture[key] || []).map(compact).filter(Boolean)
        : culture[key]
    ]));
    const payload = { companyCulture };
    const savedCultureSignalsComplete = cultureSignalFields.every(({ key }) => compact(culture[key]));

    try {
      setSavingTab('culture');
      const response = await employerAPI.updateProfile(payload);
      toast.success('Culture saved.');
      dirtyCultureFieldsRef.current = new Set();
      setProfile(response?.employer || profile);
      setCulture((previous) => ({ ...previous, ...companyCulture }));
      if (savedCultureSignalsComplete) setCultureSignalsReviewing(false);
      await refreshEnrichment();
    } catch (error) {
      toast.error(error?.message || 'Culture save failed.');
    } finally {
      setSavingTab('');
    }
  };

  const buildCompanyQuestionPayloadFor = (question, { requireAnswer = true } = {}) => {
    const nextErrors = {};
    if (!question) return { payload: [], nextErrors };

    const skipped = Boolean(skippedAnswers[question.questionKey]);
    const rawValue = questionAnswers[question.questionKey];
    const value = toAnswerValue(question, rawValue);

    if (question.answerType === 'object_list' && !skipped && typeof rawValue === 'string' && compact(rawValue) && value === null) {
      nextErrors[question.questionKey] = 'Use the benefit fields or remove the invalid entry.';
      return { payload: [], nextErrors };
    }

    if (!skipped && value === null) {
      if (requireAnswer) nextErrors[question.questionKey] = 'Answer this question or choose Skip.';
      return { payload: [], nextErrors };
    }

    return {
      payload: [{ questionKey: question.questionKey, answerValue: skipped ? null : value, skipped }],
      nextErrors
    };
  };

  const applySavedCompanyQuestion = (question, result) => {
    const savedAnswer = (result?.answers || []).find((answer) => answer.questionKey === question.questionKey);

    setProfileSetup((previous) => {
      if (!previous) return previous;

      return {
        ...previous,
        questionProgress: result?.questionProgress || previous.questionProgress,
        companyCulture: result?.companyCulture || previous.companyCulture,
        profileContext: result?.profileContext || previous.profileContext,
        questions: (previous.questions || []).map((item) => {
          if (item.questionKey !== question.questionKey || !savedAnswer) return item;

          return {
            ...item,
            answer: {
              id: savedAnswer.id,
              value: savedAnswer.answerValue,
              labels: savedAnswer.answerLabels || savedAnswer.labels || [],
              skipped: Boolean(savedAnswer.skipped),
              answeredAt: savedAnswer.answeredAt,
              skippedAt: savedAnswer.skippedAt,
              updatedAt: savedAnswer.updatedAt
            }
          };
        })
      };
    });

    if (savedAnswer) {
      setQuestionAnswers((previous) => ({
        ...previous,
        [question.questionKey]: questionInitialValue({
          ...question,
          answer: {
            value: savedAnswer.answerValue,
            skipped: savedAnswer.skipped
          }
        })
      }));
      setSkippedAnswers((previous) => ({ ...previous, [question.questionKey]: Boolean(savedAnswer.skipped) }));
    }

    setQuestionErrors((previous) => {
      const next = { ...previous };
      delete next[question.questionKey];
      return next;
    });
    setDirtyQuestionKeys((previous) => {
      const next = { ...previous };
      delete next[question.questionKey];
      return next;
    });
    dirtyQuestionKeysRef.current.delete(question.questionKey);
    setRecentlySavedQuestionKeys((previous) => ({ ...previous, [question.questionKey]: true }));
  };

  const saveCompanyQuestion = async (question, { advance = false, finish = false, showToast = true } = {}) => {
    if (createMode) {
      toast.error('Create the company profile first.');
      setActiveTab('overview');
      return false;
    }

    if (!question) return false;

    const { payload, nextErrors } = buildCompanyQuestionPayloadFor(question);
    if (Object.keys(nextErrors).length) {
      setQuestionErrors((previous) => ({ ...previous, ...nextErrors }));
      return false;
    }

    try {
      setSavingQuestionKey(question.questionKey);
      const response = await employerAPI.saveProfileAnswers(payload);
      const result = response?.data || response;
      applySavedCompanyQuestion(question, result);
      await refreshEnrichment();

      if (advance && profileQuestionCount > 1) {
        const savedIndex = profileQuestions.findIndex((item) => item.questionKey === question.questionKey);
        setActiveCompanyQuestionIndex(Math.min(Math.max(savedIndex, 0) + 1, profileQuestionCount - 1));
      }

      if (finish) setCompanyQuestionnaireFinished(true);
      if (showToast) toast.success('Answer saved.');
      return true;
    } catch (error) {
      toast.error(error?.message || 'Company profile answer save failed.');
      return false;
    } finally {
      setSavingQuestionKey('');
    }
  };

  const getCompanyQuestionStatus = (question) => {
    if (!question) return 'empty';

    const key = question.questionKey;
    const skipped = Boolean(skippedAnswers[key]);
    const draftValue = toAnswerValue(question, questionAnswers[key]);

    if (savingQuestionKey === key) return 'saving';
    if (dirtyQuestionKeys[key]) return skipped || draftValue !== null ? 'draft' : 'empty';
    if (skipped || question.answer?.skipped) return 'skipped';
    if (recentlySavedQuestionKeys[key] || question.answer) return 'saved';
    if (draftValue !== null) return 'draft';
    return 'empty';
  };

  const goToCompanyQuestion = (nextIndex) => {
    if (nextIndex < 0 || nextIndex >= profileQuestionCount || nextIndex === activeCompanyQuestionIndex) return;
    setCompanyQuestionnaireFinished(false);
    setActiveCompanyQuestionIndex(nextIndex);
  };

  const handleCurrentQuestionSkip = (checked) => {
    if (!currentCompanyQuestionKey) return;

    setCompanyQuestionnaireFinished(false);
    setSkippedAnswers((previous) => ({ ...previous, [currentCompanyQuestionKey]: checked }));
    dirtyQuestionKeysRef.current.add(currentCompanyQuestionKey);
    setDirtyQuestionKeys((previous) => ({ ...previous, [currentCompanyQuestionKey]: true }));
    setRecentlySavedQuestionKeys((previous) => {
      if (!previous[currentCompanyQuestionKey]) return previous;
      const next = { ...previous };
      delete next[currentCompanyQuestionKey];
      return next;
    });
    if (questionErrors[currentCompanyQuestionKey]) {
      setQuestionErrors((previous) => ({ ...previous, [currentCompanyQuestionKey]: '' }));
    }
  };

  const saveSections = async (event) => {
    event.preventDefault();
    if (createMode) {
      toast.error('Create the company profile first.');
      setActiveTab('overview');
      return;
    }

    try {
      setSavingTab('sections');
      const payload = sections.map((section, index) => ({
        ...section,
        displayOrder: Number(section.displayOrder ?? index),
        mediaUrls: (section.mediaUrls || []).map(compact).filter(Boolean)
      }));
      const response = await employerAPI.updateProfileSections(payload);
      setSections(hydrateSections(response?.sections || payload));
      toast.success('Profile sections saved.');
      await refreshEnrichment();
    } catch (error) {
      toast.error(error?.message || 'Profile section save failed.');
    } finally {
      setSavingTab('');
    }
  };

  const saveNewsAndLinks = async (event) => {
    event.preventDefault();
    if (createMode) {
      toast.error('Create the company profile first.');
      setActiveTab('overview');
      return;
    }

    const dirtySocialKeys = Array.from(dirtySocialFieldsRef.current);
    const payload = {
      companyNews: news
        .map((item) => ({
          title: compact(item.title),
          content: compact(item.content),
          date: item.date || null,
          url: compact(item.url)
        }))
        .filter((item) => item.title)
    };
    if (dirtySocialKeys.length) {
      payload.socialLinks = Object.fromEntries(dirtySocialKeys.map((key) => [key, compact(socialLinks[key])]));
    }

    try {
      setSavingTab('news');
      const response = await employerAPI.updateProfile(payload);
      toast.success('News and links saved.');
      dirtySocialFieldsRef.current = new Set();
      setProfile(response?.employer || profile);
      if (payload.socialLinks) {
        setSocialLinks((previous) => ({ ...previous, ...payload.socialLinks }));
        await refreshEnrichment();
      }
      setNews(payload.companyNews.map((item) => ({ ...item, date: normalizeDate(item.date) })));
    } catch (error) {
      toast.error(error?.message || 'News and links save failed.');
    } finally {
      setSavingTab('');
    }
  };

  const updateEmail2fa = async (enabled) => {
    if (securitySaving) return;

    try {
      setSecuritySaving(true);
      const response = await authAPI.updateProfile({ email2faEnabled: enabled });
      updateUser(response.user || { email2faEnabled: enabled });
      toast.success(enabled ? 'Email 2FA enabled.' : 'Email 2FA disabled.');
    } catch (error) {
      toast.error(error?.message || 'Security preference update failed.');
    } finally {
      setSecuritySaving(false);
    }
  };

  const handleLogoFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      toast.error('Upload a JPEG, PNG, GIF, SVG, or WebP logo.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be under 5MB.');
      return;
    }

    try {
      setLogoUploading(true);
      const response = await employerAPI.uploadLogo(file);
      setProfile((previous) => previous ? {
        ...previous,
        companyLogoUrl: response?.logoUrl || previous.companyLogoUrl
      } : previous);
      toast.success('Logo uploaded.');
    } catch (error) {
      toast.error(error?.message || 'Logo upload failed.');
    } finally {
      setLogoUploading(false);
      event.target.value = '';
    }
  };

  const updateCultureList = (key, index, value) => {
    dirtyCultureFieldsRef.current.add(key);
    setCulture((previous) => ({
      ...previous,
      [key]: previous[key].map((item, itemIndex) => itemIndex === index ? value : item)
    }));
  };

  const addCultureItem = (key) => {
    dirtyCultureFieldsRef.current.add(key);
    setCulture((previous) => ({ ...previous, [key]: [...previous[key], ''] }));
  };

  const removeCultureItem = (key, index) => {
    dirtyCultureFieldsRef.current.add(key);
    setCulture((previous) => {
      const next = previous[key].filter((_, itemIndex) => itemIndex !== index);
      return { ...previous, [key]: next.length ? next : [''] };
    });
  };

  const updateSection = (index, patch) => {
    setSections((previous) => previous.map((section, sectionIndex) => (
      sectionIndex === index ? { ...section, ...patch } : section
    )));
  };

  const addSection = () => {
    setSections((previous) => [
      ...previous,
      {
        sectionType: 'about',
        title: '',
        content: '',
        contentType: 'text',
        mediaUrls: [],
        displayOrder: previous.length,
        isPublished: true
      }
    ]);
  };

  const removeSection = (index) => {
    setSections((previous) => previous.filter((_, sectionIndex) => sectionIndex !== index));
  };

  const renderProfileQuestionInput = (question) => {
    const disabled = Boolean(skippedAnswers[question.questionKey]) || savingQuestionKey === question.questionKey;
    const value = questionAnswers[question.questionKey] ?? '';
    const choices = Array.isArray(question.choices) ? question.choices : [];
    const setValue = (nextValue) => {
      setCompanyQuestionnaireFinished(false);
      setQuestionAnswers((previous) => ({ ...previous, [question.questionKey]: nextValue }));
      dirtyQuestionKeysRef.current.add(question.questionKey);
      setDirtyQuestionKeys((previous) => ({ ...previous, [question.questionKey]: true }));
      setRecentlySavedQuestionKeys((previous) => {
        if (!previous[question.questionKey]) return previous;
        const next = { ...previous };
        delete next[question.questionKey];
        return next;
      });
      if (questionErrors[question.questionKey]) {
        setQuestionErrors((previous) => ({ ...previous, [question.questionKey]: '' }));
      }
    };

    if (question.answerType === 'boolean' || question.inputType === 'toggle') {
      return (
        <div className="grid gap-2 sm:grid-cols-2">
          {[
            { label: 'Yes', value: true },
            { label: 'No', value: false }
          ].map((option) => {
            const checked = value === option.value;
            return (
              <label
                key={option.label}
                className={`flex min-h-10 items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  checked ? 'border-sky-200 bg-sky-50 text-sky-700' : 'border-slate-200 bg-white text-slate-600'
                } ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-sky-200'}`}
              >
                <input
                  type="radio"
                  name={question.questionKey}
                  className="shrink-0 border-slate-300 text-sky-600 focus:ring-sky-500"
                  disabled={disabled}
                  checked={checked}
                  onChange={() => setValue(option.value)}
                />
                {option.label}
              </label>
            );
          })}
        </div>
      );
    }

    if (question.answerType === 'single_select') {
      return (
        <select disabled={disabled} value={value || ''} onChange={(event) => setValue(event.target.value)} className={inputClass}>
          <option value="">Select an answer</option>
          {choices.map((choice) => <option key={choice.value} value={choice.value}>{choice.label || choice.value}</option>)}
        </select>
      );
    }

    if (question.answerType === 'multi_select' || question.answerType === 'ranking') {
      const selected = asArray(value);
      const maxItems = Number(question.validationRules?.maxItems || 0);
      return (
        <div
          className="grid gap-2"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))' }}
        >
          {choices.map((choice) => {
            const checked = selected.includes(choice.value);
            const selectedIndex = selected.indexOf(choice.value);
            return (
              <label
                key={choice.value}
                className={`flex min-h-10 min-w-0 items-start gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  checked ? 'border-sky-200 bg-sky-50 text-sky-700' : 'border-slate-200 bg-white text-slate-600'
                } ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-sky-200'}`}
              >
                <input
                  type="checkbox"
                  className="mt-0.5 shrink-0 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                  disabled={disabled}
                  checked={checked}
                  onChange={() => {
                    if (!checked && maxItems && selected.length >= maxItems) {
                      setQuestionErrors((previous) => ({
                        ...previous,
                        [question.questionKey]: `Select up to ${maxItems}.`
                      }));
                      return;
                    }

                    setValue(checked ? selected.filter((item) => item !== choice.value) : [...selected, choice.value]);
                  }}
                />
                <span className="min-w-0 flex-1 whitespace-normal break-words leading-5">
                  {choice.label || choice.value}
                </span>
                {question.answerType === 'ranking' && checked && (
                  <span className="shrink-0 rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-700">
                    {selectedIndex + 1}
                  </span>
                )}
              </label>
            );
          })}
        </div>
      );
    }

    if (question.answerType === 'number' || question.answerType === 'scale') {
      return (
        <input
          type="number"
          disabled={disabled}
          value={value}
          min={question.validationRules?.min}
          max={question.validationRules?.max}
          onChange={(event) => setValue(event.target.value)}
          className={inputClass}
        />
      );
    }

    if (question.answerType === 'object_list') {
      const fields = getObjectListFields(question);
      const items = normalizeObjectListValue(question, value);
      const visibleItems = items.length ? items : [createObjectListItem(question)];
      const itemName = question.questionKey === 'company_benefits_offered' ? 'benefit' : 'item';
      const updateObjectItem = (index, field, nextFieldValue) => {
        const nextItems = visibleItems.map((item, itemIndex) => (
          itemIndex === index ? { ...item, [field]: nextFieldValue } : item
        ));
        setValue(nextItems);
      };
      const addObjectItem = () => setValue([...visibleItems, createObjectListItem(question)]);
      const removeObjectItem = (index) => {
        const nextItems = visibleItems.filter((_, itemIndex) => itemIndex !== index);
        setValue(nextItems.length ? nextItems : [createObjectListItem(question)]);
      };

      return (
        <div className="space-y-3">
          {visibleItems.map((item, index) => (
            <div key={`${question.questionKey}-${index}`} className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="mb-3 flex items-center justify-between gap-3">
                <span className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">
                  {fieldLabel(itemName)} {index + 1}
                </span>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => removeObjectItem(index)}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label={`Remove ${itemName}`}
                >
                  <PiTrashLight size={12} />
                </button>
              </div>
              <div
                className="grid gap-3"
                style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))' }}
              >
                {fields.map((field) => {
                  const isNotesField = ['notes', 'description', 'details'].includes(field);
                  return (
                    <label key={field} className={isNotesField ? 'min-w-0 sm:col-span-2' : 'min-w-0'}>
                      <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">
                        {fieldLabel(field)}
                      </span>
                      {isNotesField ? (
                        <textarea
                          rows={2}
                          disabled={disabled}
                          value={normalizeText(item[field])}
                          onChange={(event) => updateObjectItem(index, field, event.target.value)}
                          className={textareaClass}
                        />
                      ) : (
                        <input
                          type="text"
                          disabled={disabled}
                          value={normalizeText(item[field])}
                          onChange={(event) => updateObjectItem(index, field, event.target.value)}
                          className={inputClass}
                        />
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
          <button
            type="button"
            disabled={disabled}
            onClick={addObjectItem}
            className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 transition hover:border-sky-200 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <PiPlusLight size={12} /> Add {itemName}
          </button>
        </div>
      );
    }

    if (question.answerType === 'textarea' || question.inputType === 'textarea' || question.answerType === 'text_array') {
      return (
        <textarea
          rows={3}
          disabled={disabled}
          value={Array.isArray(value) ? value.join('\n') : value}
          onChange={(event) => setValue(event.target.value)}
          className={textareaClass}
        />
      );
    }

    return <input type="text" disabled={disabled} value={value} onChange={(event) => setValue(event.target.value)} className={inputClass} />;
  };

  const currentCompanyQuestionStatus = getCompanyQuestionStatus(currentCompanyQuestion);
  const currentCompanyQuestionIsSaving = savingQuestionKey === currentCompanyQuestionKey;
  const hasPreviousCompanyQuestion = safeCompanyQuestionIndex > 0;
  const hasNextCompanyQuestion = safeCompanyQuestionIndex < profileQuestionCount - 1;
  const currentCompanyQuestionHelpText = getProfileQuestionHelpText(currentCompanyQuestion);

  if (loading) {
    return (
      <div className="min-h-screen bg-white p-4 pt-16 sm:p-5 sm:pt-5 lg:p-7">
        <div className="mx-auto max-w-[1500px] space-y-5">
          <div className="rounded-lg border border-slate-200/80 bg-white p-5 shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
            <div className="h-4 w-32 rounded bg-slate-100" />
            <div className="mt-4 h-9 w-72 max-w-full rounded bg-slate-100" />
            <div className="mt-5 grid gap-3 sm:grid-cols-4">
              {[0, 1, 2, 3].map((item) => <div key={item} className="h-20 rounded-lg bg-slate-100" />)}
            </div>
          </div>
          <div className="h-16 rounded-lg bg-white" />
          <div className="h-[520px] rounded-lg bg-white" />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white p-5">
        <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 text-center shadow-[0_14px_36px_rgba(15,23,42,0.06)]">
          <PiWarningCircleLight className="mx-auto mb-3 text-sky-700" size={22} />
          <h1 className="font-display text-xl font-normal text-[#0b1f3a]">Profile data is unavailable</h1>
          <p className="mt-2 text-sm leading-6 text-slate-500">{loadError}</p>
          <button
            onClick={loadProfile}
            className="mt-5 inline-flex h-10 items-center justify-center rounded-lg bg-[#0b1f3a] px-4 text-sm font-medium text-white transition hover:bg-[#12345f]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white p-4 pt-16 sm:p-5 sm:pt-5 lg:p-7">
      <div className="mx-auto max-w-[1500px] space-y-5">
        <header className="rounded-lg border border-slate-200/80 bg-white/95 p-4 shadow-[0_14px_36px_rgba(15,23,42,0.045)] backdrop-blur sm:p-5">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex min-w-0 items-start gap-4">
              <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-[#0b1f3a] text-white">
                {logoUrl ? (
                  <img src={logoUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center font-display text-2xl">
                    {(core.companyName || 'S').charAt(0).toUpperCase()}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={logoUploading || !profile?.id}
                  className="absolute inset-0 flex items-center justify-center bg-slate-950/55 text-white opacity-0 transition hover:opacity-100 disabled:cursor-not-allowed"
                  aria-label="Upload logo"
                >
                  {logoUploading ? <PiCircleNotchLight size={14} className="animate-spin" /> : <PiCameraLight size={14} />}
                </button>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoFile} />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium uppercase tracking-[0.16em] text-sky-700">Employer profile</p>
                <h1 className="mt-1 truncate font-display text-2xl font-normal tracking-normal text-[#0b1f3a] sm:text-3xl">
                  {core.companyName || 'Company profile'}
                </h1>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs font-medium">
                  <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 ${statusClass(verificationStatus)}`}>
                    <PiSealCheckLight size={12} /> {labelize(verificationStatus)}
                  </span>
                  {core.headquartersLocation && (
                    <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2 py-1 text-slate-600">
                      <PiMapPinLight size={12} /> {core.headquartersLocation}
                    </span>
                  )}
                  {core.websiteUrl && (
                    <a
                      href={core.websiteUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2 py-1 text-slate-600 transition hover:text-sky-700"
                    >
                      <PiArrowSquareOutLight size={12} /> Website
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3 xl:w-[430px]">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">Completion</div>
                <div className="mt-1 font-display text-2xl font-normal text-[#0b1f3a]">{completion}%</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">Active jobs</div>
                <div className="mt-1 font-display text-2xl font-normal text-[#0b1f3a]">{profile?.activeJobs || 0}</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">Sections</div>
                <div className="mt-1 font-display text-2xl font-normal text-[#0b1f3a]">{sections.length}</div>
              </div>
            </div>
          </div>
        </header>

        {enrichment?.run && (
          <section className="overflow-hidden rounded-lg border border-sky-200 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
            <div className="flex flex-col gap-3 border-b border-sky-100 bg-sky-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
              <div className="flex min-w-0 items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-sky-700 ring-1 ring-sky-100">
                  {['pending', 'processing'].includes(enrichment.run.status)
                    ? <PiCircleNotchLight size={14} className="animate-spin" />
                    : <PiSparkleLight size={14} />}
                </span>
                <div className="min-w-0">
                  <h2 className="font-display text-lg font-normal text-[#0b1f3a]">AI company profile suggestions</h2>
                  <p className="mt-0.5 text-sm text-slate-600">
                    {getEnrichmentStatusMessage(enrichment)}
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                {enrichment.safeAcceptableCount > 0 && (
                  <button
                    type="button"
                    disabled={Boolean(enrichmentAction)}
                    onClick={acceptSafeEnrichment}
                    className="inline-flex h-9 items-center gap-2 rounded-lg bg-[#0b1f3a] px-3 text-sm font-medium text-white transition hover:bg-[#12345f] disabled:opacity-60"
                  >
                    {enrichmentAction === 'accept-safe' ? <PiCircleNotchLight size={12} className="animate-spin" /> : <PiCheckCircleLight size={12} />}
                    Accept verified facts
                  </button>
                )}
                {['failed', 'partial'].includes(enrichment.run.status) && (
                  <button
                    type="button"
                    disabled={Boolean(enrichmentAction)}
                    onClick={retryEnrichment}
                    className="inline-flex h-9 items-center gap-2 rounded-lg border border-sky-200 bg-white px-3 text-sm font-medium text-sky-700 transition hover:border-sky-300 disabled:opacity-60"
                  >
                    {enrichmentAction === 'retry' ? <PiCircleNotchLight size={12} className="animate-spin" /> : <PiSparkleLight size={12} />}
                    Retry analysis
                  </button>
                )}
              </div>
            </div>

            {enrichment.run.errorMessage && (
              <div className="border-b border-amber-100 bg-amber-50 px-4 py-2 text-sm text-amber-800 sm:px-5">
                {enrichment.run.errorMessage}
              </div>
            )}

            {enrichment.pendingCount > 0 && (
              <div className="grid max-h-[430px] gap-3 overflow-y-auto p-4 sm:grid-cols-2 sm:p-5 xl:grid-cols-3">
                {(enrichment.proposals || []).filter((proposal) => proposal.status === 'pending').map((proposal) => (
                  <article key={proposal.id} className="flex min-w-0 flex-col rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="truncate text-xs font-semibold uppercase tracking-[0.12em] text-sky-700">
                        {fieldLabel(proposal.targetKey)}
                      </span>
                      <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-slate-500 ring-1 ring-slate-200">
                        {Math.round((proposal.confidence || 0) * 100)}%
                      </span>
                    </div>
                    <p className="mt-2 line-clamp-5 whitespace-pre-wrap break-words text-sm leading-6 text-slate-700">
                      {formatSuggestionValue(proposal.proposedValue)}
                    </p>
                    {proposal.evidence?.[0]?.snippet && (
                      <p className="mt-2 line-clamp-3 border-l-2 border-sky-200 pl-2 text-xs leading-5 text-slate-500">
                        {proposal.evidence[0].snippet}
                      </p>
                    )}
                    <div className="mt-auto flex gap-2 pt-3">
                      <button
                        type="button"
                        disabled={Boolean(enrichmentAction)}
                        onClick={() => reviewEnrichmentProposal(proposal.id, 'accept')}
                        className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg bg-sky-600 px-2 text-xs font-medium text-white transition hover:bg-sky-700 disabled:opacity-60"
                      >
                        {enrichmentAction === `accept:${proposal.id}` ? <PiCircleNotchLight size={12} className="animate-spin" /> : <PiCheckLight size={12} />}
                        Accept
                      </button>
                      <button
                        type="button"
                        disabled={Boolean(enrichmentAction)}
                        onClick={() => reviewEnrichmentProposal(proposal.id, 'reject')}
                        className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2 text-xs font-medium text-slate-600 transition hover:border-rose-200 hover:text-rose-600 disabled:opacity-60"
                      >
                        {enrichmentAction === `reject:${proposal.id}` ? <PiCircleNotchLight size={12} className="animate-spin" /> : <PiTrashLight size={12} />}
                        Reject
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}

        <section className="rounded-lg border border-slate-200/80 bg-white p-2 shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
          <nav className="scrollbar-hide flex gap-1 overflow-x-auto" aria-label="Profile sections">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => selectProfileTab(tab.id)}
                  className={`inline-flex h-11 shrink-0 items-center gap-2 rounded-lg px-3.5 text-sm font-medium transition ${
                    active ? 'bg-[#eef9ff] text-sky-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                  aria-current={active ? 'page' : undefined}
                >
                  <Icon size={16} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </section>

        <main className="min-w-0 space-y-5">
            {activeTab === 'overview' && (
              <form onSubmit={saveCore} className="space-y-5">
                <Panel
                  title="Company Basics"
                  icon={PiBuildingsLight}
                  action={<SaveButton loading={savingTab === 'overview'}>{createMode ? 'Create' : 'Save'}</SaveButton>}
                >
                  {createMode && (
                    <div className="mb-4 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm font-medium text-sky-800">
                      Complete the required company profile before profile sections and logo uploads are available.
                    </div>
                  )}
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    <Field label="Company name" error={errors.companyName}>
                      <input className={inputClass} value={core.companyName} onChange={(event) => updateCore('companyName', event.target.value)} />
                    </Field>
                    <Field label="Admin role">
                      <input className={inputClass} value={core.jobTitle} onChange={(event) => updateCore('jobTitle', event.target.value)} />
                    </Field>
                    <Field label="Industry" error={errors.industry}>
                      <select className={inputClass} value={core.industry} onChange={(event) => updateCore('industry', event.target.value)}>
                        <option value="">Select industry</option>
                        {industries.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                      </select>
                    </Field>
                    <Field label="Sector">
                      <input className={inputClass} value={core.sector} onChange={(event) => updateCore('sector', event.target.value)} />
                    </Field>
                    <Field label="Company size" error={errors.companySize}>
                      <select className={inputClass} value={core.companySize} onChange={(event) => updateCore('companySize', event.target.value)}>
                        <option value="">Select size</option>
                        {companySizes.map((size) => <option key={size} value={size}>{size}</option>)}
                      </select>
                    </Field>
                    <Field label="Employee range">
                      <input className={inputClass} value={core.employeeCountRange} onChange={(event) => updateCore('employeeCountRange', event.target.value)} />
                    </Field>
                    <Field label="Company type" error={errors.companyType}>
                      <select className={inputClass} value={core.companyType} onChange={(event) => updateCore('companyType', event.target.value)}>
                        <option value="">Select type</option>
                        {companyTypes.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                      </select>
                    </Field>
                    <Field label="Founded" error={errors.foundedYear}>
                      <input className={inputClass} type="number" min="1800" max={new Date().getFullYear()} value={core.foundedYear} onChange={(event) => updateCore('foundedYear', event.target.value)} />
                    </Field>
                    <Field label="Country code" error={errors.countryCode}>
                      <input
                        className={inputClass}
                        value={core.countryCode}
                        maxLength={6}
                        inputMode="tel"
                        placeholder="UK"
                        onChange={(event) => updateCore('countryCode', sanitizeCountryCodeInput(event.target.value))}
                      />
                    </Field>
                    <Field label="Headquarters" className="xl:col-span-2" error={errors.headquartersLocation}>
                      <input className={inputClass} value={core.headquartersLocation} onChange={(event) => updateCore('headquartersLocation', event.target.value)} />
                    </Field>
                    <Field label="Website">
                      <input className={inputClass} value={core.websiteUrl} onChange={(event) => updateCore('websiteUrl', event.target.value)} />
                    </Field>
                    <Field label="Contact email" error={errors.contactEmail}>
                      <input className={inputClass} type="email" value={core.contactEmail} onChange={(event) => updateCore('contactEmail', event.target.value)} />
                    </Field>
                    <Field label="Contact phone">
                      <input className={inputClass} value={core.contactPhone} onChange={(event) => updateCore('contactPhone', event.target.value)} />
                    </Field>
                    <Field label="Registration no.">
                      <input className={inputClass} value={core.businessRegistrationNumber} onChange={(event) => updateCore('businessRegistrationNumber', event.target.value)} />
                    </Field>
                    <Field label="Tax ID">
                      <input className={inputClass} value={core.taxIdentificationNumber} onChange={(event) => updateCore('taxIdentificationNumber', event.target.value)} />
                    </Field>
                  </div>
                </Panel>

                <Panel title="Narrative" icon={PiInfoLight} action={<SaveButton loading={savingTab === 'overview'} />}>
                  <div className="grid gap-4 lg:grid-cols-2">
                    <Field label="Company description" error={errors.companyDescription}>
                      <textarea className={textareaClass} value={core.companyDescription} onChange={(event) => updateCore('companyDescription', event.target.value)} />
                    </Field>
                    <Field label="Goals and vision">
                      <textarea className={textareaClass} value={core.goalsAndVision} onChange={(event) => updateCore('goalsAndVision', event.target.value)} />
                    </Field>
                  </div>
                </Panel>

                <Panel title="Registered Address" icon={PiMapPinLight} action={<SaveButton loading={savingTab === 'overview'} />}>
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
                    <Field label="Street" className="xl:col-span-2">
                      <input className={inputClass} value={core.registeredAddress.street} onChange={(event) => updateAddress('street', event.target.value)} />
                    </Field>
                    <Field label="City">
                      <input className={inputClass} value={core.registeredAddress.city} onChange={(event) => updateAddress('city', event.target.value)} />
                    </Field>
                    <Field label="State">
                      <input className={inputClass} value={core.registeredAddress.state} onChange={(event) => updateAddress('state', event.target.value)} />
                    </Field>
                    <Field label="Postal code">
                      <input className={inputClass} value={core.registeredAddress.postalCode} onChange={(event) => updateAddress('postalCode', event.target.value)} />
                    </Field>
                  </div>
                </Panel>
              </form>
            )}

            {activeTab === 'culture' && (
              <form onSubmit={saveCulture} className="space-y-5">
                <Panel
                  id="company-profile-questions"
                  title="Company Profile Questions"
                  icon={PiClipboardTextLight}
                  headerClassName="xl:sticky xl:top-0 xl:z-20 xl:rounded-t-lg xl:bg-white/95 xl:shadow-[0_14px_34px_rgba(15,23,42,0.08)] xl:backdrop-blur"
                >
                  {createMode ? (
                    <EmptyState label="Create the company profile before adding company questions." />
                  ) : profileQuestionCount ? (
                    companyQuestionnaireFinished ? (
                      <section className="rounded-lg border border-slate-200 bg-slate-50 p-4 sm:p-5">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                          <div className="flex min-w-0 items-start gap-3">
                            <span className="flex shrink-0 items-center justify-center text-emerald-600">
                              <PiCheckCircleLight size={30} />
                            </span>
                            <div className="min-w-0">
                              <p className="text-xs font-medium uppercase tracking-[0.14em] text-slate-400">Company knowledge base</p>
                              <h3 className="mt-1 font-display text-xl font-normal text-[#0b1f3a]">Questionnaire saved</h3>
                              <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-600">
                                Company context is now available for job AI responses and matching.
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setCompanyQuestionnaireFinished(false);
                              setActiveCompanyQuestionIndex(0);
                            }}
                            className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 transition hover:border-sky-200 hover:text-sky-700"
                          >
                            Review answers
                          </button>
                        </div>
                        <div className="mt-5 overflow-hidden rounded-lg border border-slate-200 bg-white">
                          <div className="grid sm:grid-cols-3">
                            <div className="border-b border-slate-100 px-4 py-3 sm:border-b-0 sm:border-r">
                              <div className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Answered</div>
                              <div className="mt-1 font-display text-2xl font-normal text-[#0b1f3a]">
                                {companyQuestionProgress.answeredQuestions || 0}
                              </div>
                            </div>
                            <div className="border-b border-slate-100 px-4 py-3 sm:border-b-0 sm:border-r">
                              <div className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Skipped</div>
                              <div className="mt-1 font-display text-2xl font-normal text-[#0b1f3a]">
                                {companyQuestionProgress.skippedQuestions || 0}
                              </div>
                            </div>
                            <div className="px-4 py-3">
                              <div className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Completion</div>
                              <div className="mt-1 font-display text-2xl font-normal text-[#0b1f3a]">
                                {companyQuestionProgress.completionPercentage || 0}%
                              </div>
                            </div>
                          </div>
                        </div>
                      </section>
	                    ) : (
	                      <div className="space-y-5">
	                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
	                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-slate-500">
                              <span className="text-[#0b1f3a]">
                                {companyQuestionProgress.answeredQuestions || 0} of {companyQuestionProgress.totalQuestions || profileQuestionCount} answered
                              </span>
                              <span>{companyQuestionProgress.completionPercentage || 0}% complete</span>
                            </div>
                            <div className="mt-2 h-2 overflow-hidden rounded-full bg-white ring-1 ring-slate-200">
                              <div
                                className="h-full rounded-full bg-sky-500 transition-all"
                                style={{ width: `${companyQuestionProgress.completionPercentage || 0}%` }}
                              />
                            </div>
                          </div>
                          <div className="shrink-0 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-[#0b1f3a]">
                            Question {safeCompanyQuestionIndex + 1} / {profileQuestionCount}
                          </div>
                        </div>
                        <div className="mt-3 border-t border-slate-200 pt-3">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-medium uppercase tracking-[0.14em] text-slate-400">Questions</span>
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {profileQuestions.map((question, index) => {
                              const status = getCompanyQuestionStatus(question);
                              const active = index === safeCompanyQuestionIndex;
                              const statusClass = active
                                ? 'border-[#0b1f3a] bg-[#0b1f3a] text-white'
                                : status === 'saved'
                                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                  : status === 'draft'
                                    ? 'border-amber-200 bg-amber-50 text-amber-700'
                                    : status === 'skipped'
                                      ? 'border-slate-200 bg-slate-100 text-slate-500'
                                      : 'border-slate-200 bg-white text-slate-500 hover:border-sky-200 hover:text-sky-700';

                              return (
                                <button
                                  key={question.questionKey}
                                  type="button"
                                  onClick={() => goToCompanyQuestion(index)}
                                  disabled={Boolean(savingQuestionKey)}
                                  className={`flex h-9 min-w-9 items-center justify-center rounded-lg border px-2 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${statusClass}`}
                                  aria-label={`Go to question ${index + 1}`}
                                >
                                  {status === 'saving' ? <PiCircleNotchLight size={12} className="animate-spin" /> : index + 1}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-5">
                        <section className="min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50 p-4 sm:p-5">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium uppercase tracking-[0.14em] text-slate-400">{currentCompanyQuestionSection}</p>
                              <h3 className="mt-2 break-words font-display text-xl font-normal leading-7 text-[#0b1f3a]">
                                {currentCompanyQuestion.prompt}
                              </h3>
                              {currentCompanyQuestionHelpText && (
                                <p className="mt-2 break-words text-sm leading-6 text-slate-500">
                                  {currentCompanyQuestionHelpText}
                                </p>
                              )}
                            </div>
                            <label className="inline-flex h-9 shrink-0 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-500">
                              <input
                                type="checkbox"
                                checked={Boolean(skippedAnswers[currentCompanyQuestionKey])}
                                disabled={currentCompanyQuestionIsSaving}
                                onChange={(event) => handleCurrentQuestionSkip(event.target.checked)}
                                className="rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                              />
                              Skip
                            </label>
                          </div>

                          <div className="mt-6">
                            {renderProfileQuestionInput(currentCompanyQuestion)}
                            {questionErrors[currentCompanyQuestionKey] && (
                              <p className="mt-2 text-xs font-medium text-rose-600">{questionErrors[currentCompanyQuestionKey]}</p>
                            )}
                          </div>

                          <div className="mt-6 flex flex-col gap-3 border-t border-slate-200 pt-4 lg:flex-row lg:items-center lg:justify-between">
                            <div className="text-xs font-medium text-slate-500">
                              {currentCompanyQuestionIsSaving ? 'Saving...' : currentCompanyQuestionStatus === 'saved' ? 'Saved' : currentCompanyQuestionStatus === 'skipped' ? 'Skipped' : currentCompanyQuestionStatus === 'draft' ? 'Unsaved changes' : 'Not answered'}
                            </div>
                            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                              <button
                                type="button"
                                onClick={() => goToCompanyQuestion(safeCompanyQuestionIndex - 1)}
                                disabled={!hasPreviousCompanyQuestion || currentCompanyQuestionIsSaving}
                                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 transition hover:border-sky-200 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <PiCaretLeftLight size={13} /> Back
                              </button>
                              <button
                                type="button"
                                onClick={() => saveCompanyQuestion(currentCompanyQuestion, {
                                  advance: hasNextCompanyQuestion,
                                  finish: !hasNextCompanyQuestion,
                                  showToast: false
                                })}
                                disabled={Boolean(savingQuestionKey)}
                                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#0b1f3a] px-4 text-sm font-medium text-white transition hover:bg-[#12345f] disabled:cursor-not-allowed disabled:opacity-65"
                              >
                                {currentCompanyQuestionIsSaving ? <PiCircleNotchLight size={13} className="animate-spin" /> : <PiFloppyDiskLight size={13} />}
                                {hasNextCompanyQuestion ? 'Save and next' : 'Save and finish'}
                              </button>
                            </div>
                          </div>
                        </section>
                      </div>
                      </div>
                    )
                  ) : (
                    <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-500">
                      <PiWarningCircleLight size={13} className="mt-0.5 shrink-0" />
                      No company profile question bank is available from the backend.
                    </div>
                  )}
                </Panel>

                <Panel
                  title="Culture Signals"
                  icon={PiHandshakeLight}
                  action={<SaveButton loading={savingTab === 'culture'} />}
                >
                  {cultureSignalStatus.complete && !cultureSignalsReviewing ? (
                    <section className="rounded-lg border border-slate-200 bg-slate-50 p-4 sm:p-5">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="flex min-w-0 items-start gap-3">
                          <span className="flex shrink-0 items-center justify-center text-emerald-600">
                            <PiCheckCircleLight size={30} />
                          </span>
                          <div className="min-w-0">
                            <p className="text-xs font-medium uppercase tracking-[0.14em] text-slate-400">Culture signals</p>
                            <h3 className="mt-1 font-display text-xl font-normal text-[#0b1f3a]">Signals saved</h3>
                            <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-600">
                              These culture details are available for matching context and employer profile quality.
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setCultureSignalsReviewing(true)}
                          className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 transition hover:border-sky-200 hover:text-sky-700"
                        >
                          Review signals
                        </button>
                      </div>
                      <div className="mt-5 overflow-hidden rounded-lg border border-slate-200 bg-white">
                        <div className="grid sm:grid-cols-3">
                          <div className="border-b border-slate-100 px-4 py-3 sm:border-b-0 sm:border-r">
                            <div className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Completed</div>
                            <div className="mt-1 font-display text-2xl font-normal text-[#0b1f3a]">
                              {cultureSignalStatus.completed}/{cultureSignalStatus.total}
                            </div>
                          </div>
                          <div className="border-b border-slate-100 px-4 py-3 sm:border-b-0 sm:border-r">
                            <div className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Remaining</div>
                            <div className="mt-1 font-display text-2xl font-normal text-[#0b1f3a]">
                              {cultureSignalStatus.remaining}
                            </div>
                          </div>
                          <div className="px-4 py-3">
                            <div className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Readiness</div>
                            <div className="mt-1 font-display text-2xl font-normal text-[#0b1f3a]">
                              {cultureSignalStatus.percentage}%
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>
                  ) : (
                    <div className="space-y-4">
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-medium text-slate-500">
                          <span className="text-[#0b1f3a]">
                            {cultureSignalStatus.completed} of {cultureSignalStatus.total} signals complete
                          </span>
                          <span>{cultureSignalStatus.percentage}% complete</span>
                        </div>
                        <div className="mt-2 h-2 overflow-hidden rounded-full bg-white ring-1 ring-slate-200">
                          <div
                            className="h-full rounded-full bg-sky-500 transition-all"
                            style={{ width: `${cultureSignalStatus.percentage}%` }}
                          />
                        </div>
                      </div>
                      <div className="grid gap-4 lg:grid-cols-2">
                        {cultureSignalFields.map((field) => (
                          <Field key={field.key} label={field.label}>
                            <textarea
                              className={textareaClass}
                              value={culture[field.key]}
                              onChange={(event) => updateCultureSignal(field.key, event.target.value)}
                            />
                          </Field>
                        ))}
                      </div>
                    </div>
                  )}
                </Panel>

                <div className="grid gap-5 lg:grid-cols-2">
                  {[
                    ['values', 'Company Values'],
                    ['benefits', 'Benefits']
                  ].map(([key, title]) => (
                    <Panel
                      key={key}
                      title={title}
                      icon={key === 'values' ? PiHandshakeLight : PiBriefcaseLight}
                      action={
                        <button
                          type="button"
                          onClick={() => addCultureItem(key)}
                          className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-xs font-medium text-slate-600 transition hover:border-sky-200 hover:text-sky-700"
                        >
                          <PiPlusLight size={12} /> Add
                        </button>
                      }
                    >
                      <div className="space-y-3">
                        {culture[key].map((item, index) => (
                          <div key={`${key}-${index}`} className="flex gap-2">
                            <input
                              className={inputClass}
                              value={item}
                              onChange={(event) => updateCultureList(key, index, event.target.value)}
                            />
                            <button
                              type="button"
                              onClick={() => removeCultureItem(key, index)}
                              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600"
                              aria-label="Remove"
                            >
                              <PiTrashLight size={13} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </Panel>
                  ))}
                </div>
              </form>
            )}

            {activeTab === 'sections' && (
              <form onSubmit={saveSections} className="space-y-5">
                <Panel
                  title="Candidate-Facing Sections"
                  icon={PiStackLight}
                  action={
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={addSection}
                        disabled={createMode}
                        className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 text-sm font-medium text-slate-600 transition hover:border-sky-200 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <PiPlusLight size={13} /> Add
                      </button>
                      <SaveButton loading={savingTab === 'sections'} />
                    </div>
                  }
                >
                  {createMode ? (
                    <EmptyState label="Create the company profile before adding sections." />
                  ) : (
                    <div className="space-y-4">
                      {sections.map((section, index) => (
                        <div key={section.id || `new-${index}`} className="rounded-lg border border-slate-200 bg-slate-50/70 p-4">
                          <div className="grid gap-4 lg:grid-cols-[180px_minmax(0,1fr)_120px_120px_auto]">
                            <Field label="Type">
                              <select className={inputClass} value={section.sectionType} onChange={(event) => updateSection(index, { sectionType: event.target.value })}>
                                {sectionTypes.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                              </select>
                            </Field>
                            <Field label="Title">
                              <input className={inputClass} value={section.title} onChange={(event) => updateSection(index, { title: event.target.value })} />
                            </Field>
                            <Field label="Order">
                              <input className={inputClass} type="number" min="0" value={section.displayOrder} onChange={(event) => updateSection(index, { displayOrder: event.target.value })} />
                            </Field>
                            <Field label="Published">
                              <button
                                type="button"
                                onClick={() => updateSection(index, { isPublished: !section.isPublished })}
                                className={`flex h-10 w-full items-center justify-center gap-2 rounded-lg border px-3 text-sm font-medium transition ${
                                  section.isPublished ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-500'
                                }`}
                              >
                                <PiCheckCircleLight size={13} /> {section.isPublished ? 'Yes' : 'No'}
                              </button>
                            </Field>
                            <div className="flex items-end">
                              <button
                                type="button"
                                onClick={() => removeSection(index)}
                                className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600"
                                aria-label="Remove section"
                              >
                                <PiTrashLight size={13} />
                              </button>
                            </div>
                          </div>
                          <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
                            <Field label="Content">
                              <textarea className={textareaClass} value={section.content} onChange={(event) => updateSection(index, { content: event.target.value })} />
                            </Field>
                            <Field label="Media URLs">
                              <textarea
                                className={textareaClass}
                                value={(section.mediaUrls || []).join('\n')}
                                onChange={(event) => updateSection(index, { mediaUrls: event.target.value.split('\n') })}
                              />
                            </Field>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Panel>
              </form>
            )}

            {activeTab === 'news' && (
              <form onSubmit={saveNewsAndLinks} className="space-y-5">
                <Panel title="Social Links" icon={PiGlobeLight} action={<SaveButton loading={savingTab === 'news'} />}>
                  <div className="grid gap-4 md:grid-cols-2">
                    {socialFields.map(([key, label]) => (
                      <Field key={key} label={label}>
                        <div className="relative">
                          <LinkIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
                          <input
                            className={`${inputClass} pl-9`}
                            value={socialLinks[key] || ''}
                            onChange={(event) => {
                              dirtySocialFieldsRef.current.add(key);
                              setSocialLinks((previous) => ({ ...previous, [key]: event.target.value }));
                            }}
                          />
                        </div>
                      </Field>
                    ))}
                  </div>
                </Panel>

                <Panel
                  title="Company News"
                  icon={PiNewspaperLight}
                  action={
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setNews((previous) => [...previous, { title: '', content: '', date: '', url: '' }])}
                        className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 text-sm font-medium text-slate-600 transition hover:border-sky-200 hover:text-sky-700"
                      >
                        <PiPlusLight size={13} /> Add
                      </button>
                      <SaveButton loading={savingTab === 'news'} />
                    </div>
                  }
                >
                  {news.length === 0 ? (
                    <EmptyState label="No company news entries yet." />
                  ) : (
                    <div className="space-y-4">
                      {news.map((item, index) => (
                        <div key={`news-${index}`} className="rounded-lg border border-slate-200 bg-slate-50/70 p-4">
                          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_180px_auto]">
                            <Field label="Title">
                              <input
                                className={inputClass}
                                value={item.title}
                                onChange={(event) => setNews((previous) => previous.map((entry, entryIndex) => entryIndex === index ? { ...entry, title: event.target.value } : entry))}
                              />
                            </Field>
                            <Field label="Date">
                              <input
                                className={inputClass}
                                type="date"
                                value={item.date}
                                onChange={(event) => setNews((previous) => previous.map((entry, entryIndex) => entryIndex === index ? { ...entry, date: event.target.value } : entry))}
                              />
                            </Field>
                            <div className="flex items-end">
                              <button
                                type="button"
                                onClick={() => setNews((previous) => previous.filter((_, entryIndex) => entryIndex !== index))}
                                className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600"
                                aria-label="Remove news"
                              >
                                <PiTrashLight size={13} />
                              </button>
                            </div>
                          </div>
                          <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
                            <Field label="Content">
                              <textarea
                                className={textareaClass}
                                value={item.content}
                                onChange={(event) => setNews((previous) => previous.map((entry, entryIndex) => entryIndex === index ? { ...entry, content: event.target.value } : entry))}
                              />
                            </Field>
                            <Field label="URL">
                              <input
                                className={inputClass}
                                value={item.url}
                                onChange={(event) => setNews((previous) => previous.map((entry, entryIndex) => entryIndex === index ? { ...entry, url: event.target.value } : entry))}
                              />
                            </Field>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Panel>
              </form>
            )}

            {activeTab === 'security' && (
              <div className="space-y-5">
                <Panel title="Account Security" icon={PiShieldCheckLight}>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <h3 className="font-display text-lg font-normal text-[#0b1f3a]">Email two-factor verification</h3>
                        <p className="mt-1 text-sm leading-6 text-slate-600">
                          When enabled, Sanspath sends a 6 digit sign-in code to {user?.email || 'your email'} after password verification.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => updateEmail2fa(!(user?.email2faEnabled !== false))}
                        disabled={securitySaving}
                        className={`relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:cursor-not-allowed disabled:opacity-60 ${
                          user?.email2faEnabled !== false ? 'bg-sky-600' : 'bg-slate-300'
                        }`}
                        aria-pressed={user?.email2faEnabled !== false}
                      >
                        <span
                          className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition ${
                            user?.email2faEnabled !== false ? 'translate-x-7' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </Panel>
              </div>
            )}

            {activeTab === 'verification' && (
              <div className="space-y-5">
                <Panel
                  title="Verification Status"
                  icon={PiShieldCheckLight}
                  action={
                    <button
                      type="button"
                      onClick={() => navigate('/kyc-verification')}
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#0b1f3a] px-4 text-sm font-medium text-white transition hover:bg-[#12345f]"
                    >
                      <PiUploadSimpleLight size={13} /> Manage
                    </button>
                  }
                >
                  <div className="grid gap-4 md:grid-cols-4">
                    <div className={`rounded-lg border p-4 ${statusClass(verificationStatus)}`}>
                      <div className="text-xs font-medium uppercase tracking-[0.12em] opacity-70">Status</div>
                      <div className="mt-2 text-lg font-semibold">{labelize(verificationStatus)}</div>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                      <div className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Uploaded</div>
                      <div className="mt-2 font-display text-2xl font-normal text-[#0b1f3a]">{documentsUploaded}</div>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                      <div className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Verified</div>
                      <div className="mt-2 font-display text-2xl font-normal text-[#0b1f3a]">{documentsVerified}</div>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                      <div className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Required</div>
                      <div className="mt-2 font-display text-2xl font-normal text-[#0b1f3a]">{requiredDocuments}</div>
                    </div>
                  </div>
                </Panel>

                <Panel title="Documents" icon={PiClipboardTextLight}>
                  {(kyc?.documents || []).length === 0 ? (
                    <EmptyState label="No verification documents uploaded." />
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[760px] border-collapse text-left">
                        <thead>
                          <tr className="border-b border-slate-100 text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">
                            <th className="pb-3">Document</th>
                            <th className="pb-3">Status</th>
                            <th className="pb-3">Required</th>
                            <th className="pb-3">Uploaded</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(kyc?.documents || []).map((document) => (
                            <tr key={document.id} className="border-b border-slate-50 last:border-0">
                              <td className="py-3 pr-4">
                                <div className="text-sm font-medium text-[#0b1f3a]">{document.documentType}</div>
                                <div className="mt-1 text-xs text-slate-500">{document.originalFilename || document.documentDescription}</div>
                              </td>
                              <td className="py-3">
                                <span className={`inline-flex rounded-md border px-2 py-1 text-xs font-medium ${statusClass(document.verificationStatus)}`}>
                                  {labelize(document.verificationStatus)}
                                </span>
                              </td>
                              <td className="py-3 text-sm text-slate-600">{document.isRequired ? 'Yes' : 'No'}</td>
                              <td className="py-3 text-sm text-slate-600">{normalizeDate(document.createdAt) || 'Pending'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Panel>
              </div>
            )}
        </main>
      </div>
    </div>
  );
}

export default ProfilePage;
